/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { DEFAULT_HALL_ID } from 'helpers/config'
import { AppDispatch } from 'helpers/store'
import SessionAction from 'store/session/SessionAction'
import { RemindersAction } from 'store/reminders/RemindersAction'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import { toApiReminderModelName } from 'utils/reminders/reminderUtils'

export type AssignmentReminderTarget = {
    modelId: number
    previousSellerId?: number | null
    hallId?: number | null
}

export type CreateAssignmentRemindersParams = {
    modelName: ReminderModelName
    sellerId: number
    targets: AssignmentReminderTarget[]
    content?: string
}

const ASSIGNMENT_REMINDER_CONTENT = 'Assigned to you'

/**
 * Creates follow-up reminders for a newly assigned seller.
 * Skips targets where the seller did not change. No backfill of historical assigns.
 */
export const createAssignmentReminders = async (
    dispatch: AppDispatch,
    {
        modelName,
        sellerId,
        targets,
        content = ASSIGNMENT_REMINDER_CONTENT,
    }: CreateAssignmentRemindersParams
): Promise<void> => {
    if (!Number.isFinite(sellerId) || sellerId <= 0 || targets.length === 0) {
        return
    }

    const dueAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    const eligible = targets.filter((target) => {
        if (!Number.isFinite(target.modelId) || target.modelId <= 0) {
            return false
        }
        const previous = target.previousSellerId ?? null
        return previous !== sellerId
    })

    if (eligible.length === 0) {
        return
    }

    // Exception: opportunity → lead before create (RemindersAction also remaps as safety net).
    const apiModelName = toApiReminderModelName(modelName) ?? ReminderModelName.Lead

    await Promise.allSettled(
        eligible.map((target) =>
            dispatch(
                RemindersAction.fetchCreateReminder({
                    model_id: target.modelId,
                    model_name: apiModelName,
                    type: 'follow_up',
                    hall_id: target.hallId || DEFAULT_HALL_ID,
                    seller_id: sellerId,
                    interval: 1440,
                    content,
                    due_at: dueAt,
                    expires_at: null,
                })
            )
        )
    )

    dispatch(SessionAction.fetchNotifications() as any)
}

export const createAssignmentReminderForEntity = async (
    dispatch: AppDispatch,
    params: {
        modelName: ReminderModelName
        modelId: number
        sellerId: number
        previousSellerId?: number | null
        hallId?: number | null
        content?: string
    }
): Promise<void> => {
    await createAssignmentReminders(dispatch, {
        modelName: params.modelName,
        sellerId: params.sellerId,
        content: params.content,
        targets: [
            {
                modelId: params.modelId,
                previousSellerId: params.previousSellerId,
                hallId: params.hallId,
            },
        ],
    })
}
